import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Divider,
  Stack,
  Dialog,
  Drawer,
} from "@mui/material";
import { Check, Receipt } from "lucide-react";
import { differenceInDays, format, isAfter } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";

import {
  PageHeader,
  PageContainer,
  Card,
  Button,
  Badge,
  DataTable,
  EmptyState,
} from "../../components/ui";
import {
  capitalizeWords,
  formatPrice,
  isArrayNullOrUndefined,
  isNullOrUndefined,
  logMetaPixelEvent,
} from "../../utils";
import { callNonStoreApi } from "../../NonStoreApis";
import {
  FetchInstaCreditsPlansApi,
  FetchUserInstaCredits,
  StripePaymentCallback,
} from "../../Api/ApiEndPoints";
import { clientInstaPlan } from "../../Store/instagramSlice";
import InstaCreditsPricingDialog from "./InstaCreditsPricingDialog";
import useWindowSize from "../../Hooks/useWindowSize";
import {
  getCurrencyFromCountry,
  getUserCountryFromIP,
} from "../../helpers/geoLocation";

export default function PricingPage() {
  const { isMobileView } = useWindowSize();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const [currentSubscription, setCurrentSubscription] = useState({});
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stripeVerifying, setStripeVerifying] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  // Billing cadence toggle for the single Pro plan card.
  // Defaults to "yearly" (better value prop); synced to user's active cycle on load.
  const [billingToggle, setBillingToggle] = useState("yearly");
  useEffect(() => {
    if (
      currentSubscription.billingCycle === "monthly" ||
      currentSubscription.billingCycle === "yearly"
    ) {
      setBillingToggle(currentSubscription.billingCycle);
    }
  }, [currentSubscription.billingCycle]);

  const { plan: userPlan } = useSelector((state) => state.instagram.otherData);
  const { credentials } = useSelector((state) => state.instagram);
  const { apiUserData } = useSelector((state) => state.user);

  const [currency, setCurrency] = useState(
    apiUserData?.UserAdditionalDetails?.userLocation
      ? apiUserData.UserAdditionalDetails.userLocation !== "IN"
        ? "USD"
        : "INR"
      : null
  );

  useEffect(() => {
    if (currency) return;
    getUserCountryFromIP().then((countryCode) => {
      setCurrency(getCurrencyFromCountry(countryCode));
    });
  }, [currency]);

  // Handle Stripe return redirect — preserve original logic
  useEffect(() => {
    const stripeOrderId = searchParams.get("stripe_order_id");
    if (!stripeOrderId) return;

    setStripeVerifying(true);
    searchParams.delete("stripe_order_id");
    setSearchParams(searchParams, { replace: true });

    const verifyStripePayment = async () => {
      try {
        const response = await callNonStoreApi(
          `${StripePaymentCallback}?order_id=${stripeOrderId}`
        );
        if (response.data.order_status === "PAYMENT_SUCCESS") {
          dispatch(clientInstaPlan(response.data.doc));
          enqueueSnackbar("Payment Successful! Your plan has been upgraded.", {
            variant: "success",
          });
        } else {
          enqueueSnackbar(
            "Payment is being processed. It may take a moment to reflect.",
            { variant: "info", autoHideDuration: 5000 }
          );
        }
      } catch (error) {
        console.error("Stripe verification error:", error);
        enqueueSnackbar(
          "Payment verification in progress. Please refresh in a moment.",
          { variant: "info", autoHideDuration: 5000 }
        );
      } finally {
        setStripeVerifying(false);
        if (credentials?.user_id) {
          handleSetClientUserCredits();
        }
      }
    };

    verifyStripePayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetClientUserCredits = async () => {
    try {
      const response = await callNonStoreApi(FetchUserInstaCredits, {
        instaUserId: credentials.user_id,
      });
      dispatch(clientInstaPlan(response.data));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchPlans = async () => {
    try {
      const response = await callNonStoreApi(FetchInstaCreditsPlansApi);
      setPlans(response?.data?.plans || []);
    } catch (error) {
      console.log(error);
    } finally {
      setPlansLoading(false);
    }
  };

  useEffect(() => {
    logMetaPixelEvent("ViewContent");
    if (!isNullOrUndefined(credentials) && credentials.user_id) {
      handleSetClientUserCredits();
    }
    handleFetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials]);

  useEffect(() => {
    if (isNullOrUndefined(userPlan)) return;
    setCurrentSubscription(userPlan);
  }, [userPlan]);

  if (
    isNullOrUndefined(credentials) ||
    isArrayNullOrUndefined(Object.values(credentials))
  ) {
    return (
      <>
        <PageHeader
          title="Billing & Credits"
          subtitle="Manage your subscription and credit balance"
        />
        <PageContainer maxWidth="lg">
          <Card padding="lg">
            <EmptyState
              icon={Receipt}
              title="No Instagram account linked"
              description="Please link an Instagram account to view billing and credits."
            />
          </Card>
        </PageContainer>
      </>
    );
  }

  if (stripeVerifying) {
    return (
      <>
        <PageHeader
          title="Billing & Credits"
          subtitle="Manage your subscription and credit balance"
        />
        <PageContainer maxWidth="lg">
          <Card padding="lg">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "16px",
                py: "60px",
              }}
            >
              <Typography
                sx={{ fontSize: 18, fontWeight: 500, color: "#0f172a" }}
              >
                Verifying your payment...
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#64748b" }}>
                Please wait while we confirm your payment.
              </Typography>
            </Box>
          </Card>
        </PageContainer>
      </>
    );
  }

  const planType = currentSubscription.planType;
  // Free plan = quota-based (5 automations max). Not a time-limited trial.
  // Credits are the backend throttle — we expose them as "automations used".
  const isFree = planType === "free" || !planType;
  const nextBill = currentSubscription.planEndDate;

  const today = new Date();
  const expired =
    currentSubscription.planEndDate &&
    isAfter(today, new Date(currentSubscription.planEndDate));
  const isActive = planType && !isFree && !expired;

  // Free quota: 5 automations. Backend exposes remaining via totalCreditsRemaining
  // (legacy field name — 1 credit == 1 automation on the Free plan).
  const FREE_QUOTA = 5;
  const freeRemaining = Math.max(
    0,
    Math.min(FREE_QUOTA, currentSubscription.totalCreditsRemaining ?? FREE_QUOTA)
  );
  const freeUsed = FREE_QUOTA - freeRemaining;
  const atFreeLimit = isFree && freeRemaining === 0;

  const planStatusLabel = isActive
    ? capitalizeWords(planType)
    : expired
    ? capitalizeWords(planType || "")
    : "Free plan";
  const billingCycle = currentSubscription.billingCycle;

  // Build pricing tier cards from fetched plans.
  // Each plan from backend carries monthly/yearly prices in various currencies.
  // We surface Free + Monthly + Yearly tiers.
  const tierCards = buildTierCards(plans, currency);

  const transactionHistory = currentSubscription?.transactionHistory || [];

  const billingRows = transactionHistory.map((t, i) => ({
    id: t.id || `txn-${i}`,
    date: t.saleDate,
    description: `${capitalizeWords(t.plan?.billingCycle || "")} Plan`,
    amount: Object.values(t.plan?.prices || {})[0] || 0,
    amountCurrency: Object.keys(t.plan?.prices || {})[0] || currency,
    status: "paid",
    invoiceUrl: t.invoiceUrl,
  }));

  return (
    <>
      <PageHeader
        title="Billing"
        subtitle="Manage your subscription"
      />
      <PageContainer maxWidth="lg">
        {/* Plan status banner — single card, full width */}
        <Card
          padding="lg"
          variant="elevated"
          sx={{
            mb: 3,
            ...(isFree && !atFreeLimit
              ? {
                  borderColor: "#fed7aa",
                  background:
                    "linear-gradient(135deg, #fff7ed 0%, #ffffff 70%)",
                }
              : atFreeLimit || expired
              ? { borderColor: "#fecaca" }
              : {}),
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: "16px",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <Typography
                  sx={{ fontSize: 20, fontWeight: 600, color: "#0f172a" }}
                >
                  {planStatusLabel}
                </Typography>
                {isActive ? (
                  <Badge variant="success">Active</Badge>
                ) : expired ? (
                  <Badge variant="warning">Expired</Badge>
                ) : atFreeLimit ? (
                  <Badge variant="warning">Limit reached</Badge>
                ) : (
                  <Badge variant="brand">Free</Badge>
                )}
              </Box>
              <Typography sx={{ fontSize: 14, color: "#475569", mt: 0.75 }}>
                {isActive
                  ? `${capitalizeWords(billingCycle || "")} billing · Next bill ${
                      nextBill ? format(new Date(nextBill), "dd MMM yyyy") : "—"
                    }`
                  : expired
                  ? `Your plan expired${
                      nextBill
                        ? ` on ${format(new Date(nextBill), "dd MMM yyyy")}`
                        : ""
                    }. Automations are paused until you reactivate.`
                  : atFreeLimit
                  ? `You've used all ${FREE_QUOTA} free automations. Upgrade to create more.`
                  : `${freeUsed} of ${FREE_QUOTA} free automations used · ${freeRemaining} left`}
              </Typography>
              {/* Progress bar for Free quota */}
              {isFree && !expired && (
                <Box
                  sx={{
                    mt: 1.5,
                    height: 6,
                    borderRadius: 999,
                    backgroundColor: "#f1f5f9",
                    overflow: "hidden",
                    maxWidth: 360,
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${(freeUsed / FREE_QUOTA) * 100}%`,
                      backgroundColor: atFreeLimit ? "#ef4444" : "#f97316",
                      transition: "width 200ms ease-out",
                    }}
                  />
                </Box>
              )}
            </Box>
            {(isFree || expired) && (
              <Button
                variant="primary"
                size="md"
                onClick={() => {
                  logMetaPixelEvent("ButtonClick", true, {
                    button_name: "upgrade plan",
                  });
                  setShowPricingDialog(true);
                }}
                sx={{ flexShrink: 0 }}
              >
                {expired ? "Reactivate plan" : "Upgrade to Pro"}
              </Button>
            )}
          </Box>
        </Card>

        {/* Single Pro plan with Monthly / Annually toggle */}
        {(() => {
          const monthlyTier = tierCards.find((t) => t.id === "monthly");
          const yearlyTier = tierCards.find((t) => t.id === "yearly");
          const activeTier =
            billingToggle === "yearly" ? yearlyTier || monthlyTier : monthlyTier;
          if (!activeTier) return null;
          const isCurrentTier = isActive && billingCycle === billingToggle;

          // Compute yearly savings % for the toggle hint
          const monthlyPerMonth = monthlyTier?.priceLabel
            ? parseFloat(String(monthlyTier.priceLabel).replace(/[^\d.]/g, ""))
            : 0;
          const yearlyPerMonth = yearlyTier?.priceLabel
            ? parseFloat(String(yearlyTier.priceLabel).replace(/[^\d.]/g, ""))
            : 0;
          const savings =
            monthlyPerMonth > 0 && yearlyPerMonth > 0
              ? Math.max(0, Math.round((1 - yearlyPerMonth / monthlyPerMonth) * 100))
              : 0;

          const ToggleOption = ({ value, label, children }) => {
            const selected = billingToggle === value;
            return (
              <Box
                role="radio"
                aria-checked={selected}
                tabIndex={0}
                onClick={() => setBillingToggle(value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setBillingToggle(value);
                  }
                }}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  px: "16px",
                  py: "6px",
                  borderRadius: "999px",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: "pointer",
                  color: selected ? "#0f172a" : "#64748b",
                  backgroundColor: selected ? "#ffffff" : "transparent",
                  boxShadow: selected
                    ? "0 1px 2px 0 rgba(16,24,40,0.06)"
                    : "none",
                  transition: "background-color 150ms, color 150ms",
                  "&:focus-visible": {
                    outline: "2px solid #f97316",
                    outlineOffset: 2,
                  },
                }}
              >
                {label}
                {children}
              </Box>
            );
          };

          return (
            <>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: "#0f172a",
                  mb: 2,
                }}
              >
                Change plan
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <Box
                  role="radiogroup"
                  aria-label="Billing cadence"
                  sx={{
                    display: "inline-flex",
                    padding: "4px",
                    borderRadius: "999px",
                    backgroundColor: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <ToggleOption value="monthly" label="Monthly" />
                  <ToggleOption value="yearly" label="Annually">
                    {savings > 0 && (
                      <Badge variant="success">Save {savings}%</Badge>
                    )}
                  </ToggleOption>
                </Box>
              </Box>

              <Card
                padding="lg"
                sx={{
                  maxWidth: 520,
                  mx: "auto",
                  borderColor: "#f97316",
                  borderWidth: 2,
                }}
              >
                <Typography
                  sx={{ fontSize: 18, fontWeight: 600, color: "#0f172a" }}
                >
                  Pro
                </Typography>
                <Box sx={{ display: "flex", alignItems: "baseline", mt: 1 }}>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 36,
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    {activeTier.priceLabel}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 14,
                      color: "#64748b",
                      ml: 0.5,
                      whiteSpace: "nowrap",
                    }}
                  >
                    /month
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#64748b",
                    mt: 0.5,
                  }}
                >
                  {billingToggle === "yearly"
                    ? `Billed annually${
                        savings > 0 ? ` · Save ${savings}% vs. monthly` : ""
                      }`
                    : "Billed monthly · Cancel anytime"}
                </Typography>
                <Divider sx={{ my: 2, borderColor: "#e2e8f0" }} />
                <Stack spacing={1.5} sx={{ mb: 2.5 }}>
                  {activeTier.features.map((f, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Check size={16} color="#059669" />
                      <Typography sx={{ fontSize: 13, color: "#0f172a" }}>
                        {f}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  disabled={isCurrentTier || plansLoading}
                  onClick={() => {
                    logMetaPixelEvent("ButtonClick", true, {
                      button_name: "upgrade plan",
                    });
                    setShowPricingDialog(true);
                  }}
                >
                  {isCurrentTier ? "Current plan" : "Upgrade to Pro"}
                </Button>
              </Card>
            </>
          );
        })()}

        {/* Billing history */}
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 600,
            color: "#0f172a",
            mb: 2,
            mt: 4,
          }}
        >
          Billing history
        </Typography>
        <Box>
          <DataTable
            columns={[
              {
                key: "date",
                header: "Date",
                render: (r) =>
                  r.date ? format(new Date(r.date), "dd MMM yyyy") : "—",
              },
              { key: "description", header: "Description" },
              {
                key: "amount",
                header: "Amount",
                align: "right",
                render: (r) =>
                  typeof r.amount === "number"
                    ? formatPrice(r.amount, r.amountCurrency)
                    : "—",
              },
              {
                key: "status",
                header: "Status",
                render: (r) => (
                  <Badge
                    variant={
                      r.status === "paid"
                        ? "success"
                        : r.status === "pending"
                        ? "warning"
                        : "error"
                    }
                  >
                    {r.status}
                  </Badge>
                ),
              },
              {
                key: "invoice",
                header: "",
                align: "right",
                render: (r) =>
                  r.invoiceUrl ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      href={r.invoiceUrl}
                      target="_blank"
                      component="a"
                    >
                      Download
                    </Button>
                  ) : null,
              },
            ]}
            rows={billingRows}
            loading={loading}
            empty={
              <EmptyState
                icon={Receipt}
                title="No billing history"
                description="Your invoices will appear here."
              />
            }
          />
        </Box>
      </PageContainer>

      {/* Preserve existing Razorpay/Cashfree purchase dialog flow */}
      {isMobileView() ? (
        <Drawer
          onClose={() => setShowPricingDialog(false)}
          open={showPricingDialog}
          anchor="bottom"
          PaperProps={{
            sx: {
              padding: "8px 20px 20px 20px",
              borderRadius: "24px 24px 0 0",
              height: "70vh",
            },
          }}
        >
          <InstaCreditsPricingDialog
            currentSubscription={currentSubscription}
            onClose={() => setShowPricingDialog(false)}
            currency={currency}
          />
        </Drawer>
      ) : (
        <Dialog
          open={showPricingDialog}
          onClose={() => setShowPricingDialog(false)}
          PaperProps={{
            sx: {
              padding: "8px 20px 20px 20px",
              height: "70vh",
              maxWidth: "unset",
              borderRadius: "24px",
            },
          }}
        >
          <InstaCreditsPricingDialog
            currentSubscription={currentSubscription}
            onClose={() => setShowPricingDialog(false)}
            currency={currency}
          />
        </Dialog>
      )}
    </>
  );
}

/**
 * Build pricing-tier card objects from fetched plans.
 * Plans returned by backend have monthlyPrice, yearlyPrice keyed by currency
 * plus a features array. We surface Monthly + Yearly only — Trial is the
 * default state for new users, not a purchasable tier.
 */
function buildTierCards(plans, currency) {
  const firstPlan = plans?.[0];
  const features = (firstPlan?.features || [
    "Unlimited automated DMs",
    "Advanced keyword matching",
    "Leads capture & export",
  ]).map((f) =>
    typeof f === "string"
      ? f.replace(/unlimited credits?/i, "Unlimited automated DMs")
          .replace(/credits?/gi, "DMs")
      : f
  );

  const monthlyTier = firstPlan
    ? {
        id: "monthly",
        name: `${firstPlan.name || "Pro"} Monthly`,
        priceLabel: formatPrice(
          firstPlan.monthlyPrice?.[currency] ?? 0,
          currency || "USD"
        ),
        period: "/month",
        description: "All features, billed monthly.",
        features,
        ctaLabel: "Upgrade",
        highlighted: false,
      }
    : null;

  const yearlyTier = firstPlan
    ? {
        id: "yearly",
        name: `${firstPlan.name || "Pro"} Yearly`,
        priceLabel: formatPrice(
          Math.round((firstPlan.yearlyPrice?.[currency] ?? 0) / 12),
          currency || "USD"
        ),
        period: "/month",
        periodSubtext: "billed annually",
        description: `Save ${Math.round(
          (firstPlan.offer || 0) * 100
        )}% with annual billing.`,
        features,
        ctaLabel: "Upgrade",
        highlighted: true,
      }
    : null;

  return [monthlyTier, yearlyTier].filter(Boolean);
}
